import { useImgSrc } from '../img/imgSrc';
import { vars } from '../theme/vars';
import { defaultBrand } from './defaultBrand';
import { Global } from '@emotion/react';
import { createTheme, Theme, ThemeProvider, useTheme, type ThemeOptions } from '@mui/material';
import { Brand } from '@whiz-cart/node-shared/models/brand/brand';
import { defaultsDeep, omit, omitBy } from 'lodash';
import { createContext, ReactNode, useContext, useMemo } from 'react';

const omitEmpty = <T extends object>(obj: T): Partial<T> => omitBy(obj, (v) => v === '') as Partial<T>;

export const brandContext = createContext<Brand>(defaultBrand);

export function createBrandTheme(baseTheme: Theme, brand: Brand) {
    return createTheme(baseTheme as ThemeOptions, omit(brand, '_id', 'images'), {
        shape: {
            borderRadius: brand.borderRadius,
        },
    });
}

export function BrandProvider({ brand, children }: { brand?: Brand; children?: ReactNode }) {
    const resolvedBrand = useMemo(() => {
        const mergedBrand = defaultsDeep({}, brand ? omitEmpty(brand) : {}, defaultBrand);
        if (brand && (brand.customerIds?.length ?? 0) > 0) {
            mergedBrand.customerIds = brand.customerIds;
        }
        return mergedBrand;
    }, [brand]);

    const baseTheme = useTheme();
    const theme = useMemo(() => createBrandTheme(baseTheme, resolvedBrand), [baseTheme, resolvedBrand]);

    return (
        <brandContext.Provider value={resolvedBrand}>
            <ThemeProvider theme={theme}>
                <CssVarProvider>{children}</CssVarProvider>
            </ThemeProvider>
        </brandContext.Provider>
    );
}

export function CssVarProvider({ children }: { children: ReactNode }) {
    const resolveImgSrc = useImgSrc();
    const brand = useBrand();
    const theme = useTheme();

    const globalStyles = useMemo(() => {
        const palette = Object.fromEntries(
            Object.entries(theme.palette).flatMap(([key, props]) => {
                if (typeof props === 'object' && props !== null) {
                    return Object.entries(props).map(([subKey, value]) => [`--palette-${key}-${subKey}`, value as any]);
                }

                if (typeof props === 'function') {
                    return [];
                }

                return [[`--palette-${key}`, props]];
            }),
        );

        if (brand.headerUsePrimaryColor) {
            palette['--palette-headerBackground-main'] = vars.primaryMain;
            palette['--palette-headerBackground-light'] = vars.primaryLight;
            palette['--palette-headerBackground-dark'] = vars.primaryDark;
            palette['--palette-headerBackground-contrastText'] = vars.primaryContrastText;
        }

        const images = Object.fromEntries(
            Object.entries(brand.images).map(([key, image]) => [
                `--images-${key}`,
                image ? `url("${resolveImgSrc({ brandImageUrl: image }) ?? ''}")` : 'none',
            ]),
        );

        let emailImages: Record<string, string> = {};
        if (brand?.emails?.images) {
            emailImages = Object.fromEntries(
                Object.entries(brand.emails.images).map(([key, image]) => [
                    `--emails-${key}`,
                    image ? `url("${resolveImgSrc({ brandImageUrl: image }) ?? ''}")` : 'none',
                ]),
            );
        }

        const customerIds = Object.fromEntries(
            brand.customerIds?.flatMap((customerId) => {
                const prefix = `--customerId-${customerId?.styleId ?? ''}`;
                return [
                    [`${prefix}-color-main`, customerId?.color ? customerId.color.main : 'none'],
                    [`${prefix}-color-contrastText`, customerId.color.contrastText],
                    [`${prefix}-image`, customerId.image ? `url("${resolveImgSrc({ brandImageUrl: customerId.image }) ?? ''}")` : 'none'],
                    [
                        `${prefix}-logoImage`,
                        customerId.logoImage ? `url("${resolveImgSrc({ brandImageUrl: customerId.logoImage }) ?? ''}")` : 'none',
                    ],
                ];
            }) ?? [],
        );

        const primaryCustomerId =
            brand.customerIds?.find((cId) => cId.type === 'card' && cId.showOnWelcomeScreen) ?? brand.customerIds?.[0] ?? null;
        if (primaryCustomerId) {
            customerIds['--customerIdPrimary-color-main'] = primaryCustomerId.color.main;
            customerIds['--customerIdPrimary-color-contrastText'] = primaryCustomerId.color.contrastText;
            customerIds['--customerIdPrimary-image'] = primaryCustomerId.image
                ? `url("${resolveImgSrc({ brandImageUrl: primaryCustomerId.image }) ?? ''}")`
                : 'none';
            customerIds['--customerIdPrimary-logoImage'] = primaryCustomerId.logoImage
                ? `url("${resolveImgSrc({ brandImageUrl: primaryCustomerId.logoImage }) ?? ''}")`
                : 'none';
        }

        const extraProperties = {
            '--border-radius': typeof brand.borderRadius === 'number' ? `${brand.borderRadius}px` : brand.borderRadius,
            '--header-search-btn': brand.headerUsePrimaryColor ? theme.palette.primary.dark : theme.palette.primary.main,
            '--header-sos-btn': brand.headerUsePrimaryColor ? theme.palette.primary.main : 'white',
            '--header-selected-button-background': brand.headerUsePrimaryColor ? theme.palette.primary.dark : 'white',
            '--header-selected-button-contrastText': brand.headerUsePrimaryColor
                ? theme.palette.getContrastText(theme.palette.primary.dark)
                : 'var(--dark-color)',
            '--payment-header-background': brand.headerUsePrimaryColor ? theme.palette.primary.main : 'white',
            '--payment-cartId-background': brand.headerUsePrimaryColor ? theme.palette.primary.dark : theme.palette.primary.main,
            '--payment-cartId-contrastText': theme.palette.getContrastText(
                brand.headerUsePrimaryColor ? theme.palette.primary.dark : theme.palette.primary.main,
            ),
            '--amountConfigurator-background': brand.priceLayoutAlt ? theme.palette.primary.main : '#eee',
            '--header-total-display': brand.headerDisplayTotal ? 'flex' : 'none',
            '--header-bubble-display': brand.headerDisplayTotal ? 'none' : 'block',
            '--header-cart-padding': brand.headerDisplayTotal ? '0' : '1rem',
            '--font-family-alternative':
                brand.fontFamily ||
                "'Fira Sans', 'Noto Sans JP Variable', 'Noto Sans SC Variable', 'Noto Sans TC Variable', 'Noto Sans Thai Variable', sans-serif",
        };

        return {
            ':root': {
                ...palette,
                ...images,
                ...customerIds,
                ...emailImages,
                ...extraProperties,
            },
        };
    }, [theme, brand, resolveImgSrc]);

    return (
        <>
            <Global styles={globalStyles} />

            {children}
        </>
    );
}

export function useBrand() {
    const brand = useContext(brandContext);

    return useMemo(
        () =>
            ({
                ...brand,
                customerIds: brand?.customerIds?.map((customerId) => ({
                    ...customerId,
                    styleId: customerId?.id?.replaceAll('.', '-') || '',
                    logoImage:
                        customerId?.useImageAsLogoImage || customerId?.useImageAsLogoImage === undefined
                            ? customerId?.image
                            : customerId?.logoImage,
                    useImageAsLogoImage: customerId?.useImageAsLogoImage === undefined ? true : customerId?.useImageAsLogoImage,
                })),
            }) as Brand,
        [brand],
    );
}

export function UseBrand({ children }: { children: (brandAssets: ReturnType<typeof useBrand>) => ReactNode }) {
    const brand = useBrand();

    return <>{children(brand)}</>;
}
